import { z } from 'zod';
import { dataSchema, uuidSchema } from './comum.ts';
import {
  contaPrincipalDoTitulo,
  type ClassificacaoBrutaContasPagar,
} from './relatorio-contas-pagar.ts';
import {
  COMPARAR_TEXTO_BAIXAS,
  contaCorrenteResumoDeBaixasSchema,
  quebrarPorDataDaBaixa,
  ratearValoresDaBaixa,
  resumoPorContaCorrente,
  somarValoresDaBaixa,
  totaisDeBaixasSchema,
  valoresDaBaixaBruta,
  valoresDaBaixaSchema,
  type ValoresBrutosDaBaixa,
  type ValoresDaBaixa,
} from './relatorio-baixas.ts';

/**
 * Relatório de pagamentos (`PAG·04`).
 *
 * O que **saiu do caixa** para pagar títulos, baixa por baixa, **quebrado por
 * empresa** e em ordem de **data de pagamento**, com subtotal das cinco colunas
 * de valor a cada data — "quanto pagamos em cada dia, de quê e por qual conta".
 * O `PAG·03` responde a outra pergunta: o que se deve e quando vence. Este é o
 * que já foi pago.
 *
 * ## De onde vem cada linha
 *
 * De `baixas_contas_pagar`: **uma baixa, uma linha**. O título pago duas vezes
 * em dias diferentes aparece duas vezes, cada uma na sua data — é a pergunta do
 * caixa, e não a do título.
 *
 * - **A baixa estornada fica de fora.** O estorno exclui as movimentações de
 *   conta corrente dela (`.spec/016`, seção 10): o dinheiro não saiu, e o
 *   relatório de pagamentos que a contasse não bateria com o extrato.
 * - **Todas as ações entram** — `DIMINUI_SALDO`, `AUMENTA_SALDO`, `MANTEM_SALDO`.
 *   A ação diz o que a baixa faz com o saldo do **título**; nas três o dinheiro
 *   sai da conta corrente, e é o dinheiro que este relatório soma.
 *
 * ## As cinco colunas
 *
 * Bruto (`valor_baixa_saldo_titulo`), desconto, juros, outros e líquido
 * (`valor_liquido`, gravado na baixa — ver `relatorio-baixas.ts`).
 *
 * ## A conta contábil
 *
 * A baixa não tem conta: o título tem um **rateio** de contas por índice
 * (`contas_pagar_contas_contabeis`), e a baixa o herda. A linha mostra a
 * **principal** — a de maior índice, empate pelo código, a regra do `PAG·03` —
 * com "(+N)" quando há outras. O resumo por conta contábil **divide as cinco
 * colunas** de cada baixa pelo índice de cada conta, por maior resto, e fecha
 * exato no total em todas elas. Decisão do Adriano, 25/09/2026: as cinco, e não
 * só o bruto — o relatório responde "quanto desta natureza pagamos", juros
 * incluídos, e não reproduz o razão do caixa, onde as acessórias vão às contas
 * da configuração.
 *
 * ## Emissão
 *
 * A do título, pela regra do `PAG·03`: a da nota fiscal quando ele nasceu de
 * uma, e o lançamento nos outros.
 */

/* -------------------------------------------------------------------------- */
/* Teto                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Teto de baixas por consulta. O relatório não pagina e **recusa** acima dele em
 * vez de truncar — a razão do `PAG·03`: subtotal de meio dia não é subtotal.
 */
export const LIMITE_BAIXAS_RELATORIO_PAGAMENTOS = 5000;

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros do relatório. **Todos opcionais**, e a ausência é "sem recorte".
 *
 * - `empresaId` é a empresa **devedora** do título (`contas_pagar.empresa_id`).
 * - `contaCorrenteId` é a conta de onde o dinheiro saiu (`baixas_contas_pagar`).
 * - O período é o da **data do pagamento** (`data_movimentacao` da baixa),
 *   inclusivo nas duas pontas.
 *
 * "Quebrar por data" não está aqui: não muda o que se soma, só o que a tela
 * mostra. Estrito: parâmetro desconhecido é erro (ver `query-estrita`).
 */
export const relatorioPagamentosQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    fornecedorId: uuidSchema.optional(),
    contaCorrenteId: uuidSchema.optional(),
    pagamentoDe: dataSchema.optional(),
    pagamentoAte: dataSchema.optional(),
  })
  .strict();

export type RelatorioPagamentosQuery = z.infer<typeof relatorioPagamentosQuerySchema>;
/** O que o cliente manda. Igual à saída hoje, e separado por simetria com os outros relatórios. */
export type RelatorioPagamentosQueryEntrada = z.input<typeof relatorioPagamentosQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/** Uma baixa do relatório, com o título dela ao lado. */
export const linhaRelatorioPagamentosSchema = valoresDaBaixaSchema.extend({
  baixaId: uuidSchema,
  contaPagarId: uuidSchema,
  numeroDocumento: z.string(),
  parcela: z.number().int().positive(),
  totalParcelas: z.number().int().positive(),
  /** Emissão da nota, ou o lançamento do título sem nota. */
  dataEmissao: dataSchema,
  dataVencimento: dataSchema,
  dataPagamento: dataSchema,
  fornecedorId: uuidSchema,
  fornecedorNome: z.string(),
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string(),
  /** A conta de maior índice do título. `null` só no título sem classificação. */
  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  /** Quantas contas o título tem **além** da principal — o "(+2)" da coluna. */
  outrasContasContabeis: z.number().int().nonnegative(),
});

export type LinhaRelatorioPagamentos = z.infer<typeof linhaRelatorioPagamentosSchema>;

/** As baixas de uma data de pagamento, com o subtotal dela. */
export const dataRelatorioPagamentosSchema = z.object({
  dataPagamento: dataSchema,
  baixas: z.array(linhaRelatorioPagamentosSchema),
  totais: totaisDeBaixasSchema,
});

export type DataRelatorioPagamentos = z.infer<typeof dataRelatorioPagamentosSchema>;

/** A quebra por empresa. */
export const empresaRelatorioPagamentosSchema = z.object({
  empresaId: uuidSchema,
  empresaNome: z.string(),
  datas: z.array(dataRelatorioPagamentosSchema),
  totais: totaisDeBaixasSchema,
});

export type EmpresaRelatorioPagamentos = z.infer<typeof empresaRelatorioPagamentosSchema>;

/** Uma linha do resumo por fornecedor: a baixa inteira vai ao fornecedor do título. */
export const fornecedorResumoPagamentosSchema = totaisDeBaixasSchema.extend({
  fornecedorId: uuidSchema,
  fornecedorNome: z.string(),
});

export type FornecedorResumoPagamentos = z.infer<typeof fornecedorResumoPagamentosSchema>;

/**
 * Uma linha do resumo por conta contábil — as cinco colunas **rateadas** pelo
 * índice de cada conta do título. `baixas` conta as baixas que têm fatia na
 * conta; a soma da coluna passa do total quando algum título tem mais de uma.
 */
export const contaContabilResumoPagamentosSchema = totaisDeBaixasSchema.extend({
  /** `null` é "Sem classificação": o título sem conta, ou o que o rateio não cobre. */
  contaContabilId: uuidSchema.nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
});

export type ContaContabilResumoPagamentos = z.infer<typeof contaContabilResumoPagamentosSchema>;

export const resumoRelatorioPagamentosSchema = z.object({
  porFornecedor: z.array(fornecedorResumoPagamentosSchema),
  porContaContabil: z.array(contaContabilResumoPagamentosSchema),
  porContaCorrente: z.array(contaCorrenteResumoDeBaixasSchema),
});

export type ResumoRelatorioPagamentos = z.infer<typeof resumoRelatorioPagamentosSchema>;

/**
 * O relatório inteiro.
 *
 * O filtro volta **resolvido** — os nomes da empresa, do fornecedor e da conta
 * corrente —, porque o PDF imprime o cabeçalho. As datas voltam como vieram,
 * `null` incluído. Só aparecem as empresas **com** baixa no recorte.
 */
export const relatorioPagamentosSchema = z.object({
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema.nullable(),
  fornecedorNome: z.string().nullable(),
  contaCorrenteId: uuidSchema.nullable(),
  contaCorrenteDescricao: z.string().nullable(),
  pagamentoDe: dataSchema.nullable(),
  pagamentoAte: dataSchema.nullable(),
  empresas: z.array(empresaRelatorioPagamentosSchema),
  totais: totaisDeBaixasSchema,
  resumo: resumoRelatorioPagamentosSchema,
});

export type RelatorioPagamentos = z.infer<typeof relatorioPagamentosSchema>;

/* -------------------------------------------------------------------------- */
/* Montagem — do que o banco leu ao relatório                                 */
/* -------------------------------------------------------------------------- */

/** Uma baixa como o repositório a devolve, com o título, a empresa e a conta ao lado. */
export interface BaixaBrutaPagamentos extends ValoresBrutosDaBaixa {
  baixaId: string;
  contaPagarId: string;
  empresaId: string;
  empresaNome: string;
  fornecedorId: string;
  fornecedorNome: string;
  contaCorrenteId: string;
  contaCorrenteDescricao: string;
  contaCorrenteEmpresaNome: string;
  numeroDocumento: string;
  parcela: number;
  totalParcelas: number;
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento: string;
}

/** Código de conta em ordem de plano: "1.2.10" depois de "1.2.9". Sem código, no fim. */
function compararCodigo(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return COMPARAR_TEXTO_BAIXAS.compare(a, b);
}

function paraLinha(
  baixa: BaixaBrutaPagamentos,
  classificacoes: readonly ClassificacaoBrutaContasPagar[],
): LinhaRelatorioPagamentos {
  const principal = contaPrincipalDoTitulo(classificacoes);

  return {
    baixaId: baixa.baixaId,
    contaPagarId: baixa.contaPagarId,
    numeroDocumento: baixa.numeroDocumento,
    parcela: baixa.parcela,
    totalParcelas: baixa.totalParcelas,
    dataEmissao: baixa.dataEmissao,
    dataVencimento: baixa.dataVencimento,
    dataPagamento: baixa.dataPagamento,
    ...valoresDaBaixaBruta(baixa),
    fornecedorId: baixa.fornecedorId,
    fornecedorNome: baixa.fornecedorNome,
    contaCorrenteId: baixa.contaCorrenteId,
    contaCorrenteDescricao: baixa.contaCorrenteDescricao,
    contaContabilId: principal?.contaContabilId ?? null,
    contaContabilCodigo: principal?.contaContabilCodigo ?? null,
    contaContabilDescricao: principal?.contaContabilDescricao ?? null,
    outrasContasContabeis: Math.max(classificacoes.length - 1, 0),
  };
}

/**
 * A ordem dentro de uma data: fornecedor, documento, parcela.
 *
 * A data já é a quebra; dentro dela o fornecedor junta o que se pagou a cada
 * um, e documento com parcela deixa as parcelas de uma nota em sequência.
 */
function compararBaixas(a: LinhaRelatorioPagamentos, b: LinhaRelatorioPagamentos): number {
  return (
    COMPARAR_TEXTO_BAIXAS.compare(a.fornecedorNome, b.fornecedorNome) ||
    COMPARAR_TEXTO_BAIXAS.compare(a.numeroDocumento, b.numeroDocumento) ||
    a.parcela - b.parcela ||
    a.baixaId.localeCompare(b.baixaId)
  );
}

/** O resumo por fornecedor: a baixa inteira vai ao fornecedor do título. */
function resumoPorFornecedor(
  linhas: readonly LinhaRelatorioPagamentos[],
): z.infer<typeof fornecedorResumoPagamentosSchema>[] {
  const porFornecedor = new Map<string, LinhaRelatorioPagamentos[]>();
  for (const linha of linhas) {
    const doFornecedor = porFornecedor.get(linha.fornecedorId) ?? [];
    doFornecedor.push(linha);
    porFornecedor.set(linha.fornecedorId, doFornecedor);
  }

  return [...porFornecedor]
    .map(([fornecedorId, baixas]) => ({
      fornecedorId,
      fornecedorNome: (baixas[0] as LinhaRelatorioPagamentos).fornecedorNome,
      ...somarValoresDaBaixa(baixas),
    }))
    .sort((a, b) => COMPARAR_TEXTO_BAIXAS.compare(a.fornecedorNome, b.fornecedorNome));
}

const SEM_CONTA = '';

interface AcumuladoDaConta {
  conta: ClassificacaoBrutaContasPagar | null;
  fatias: ValoresDaBaixa[];
  baixas: number;
}

function acumular(
  mapa: Map<string, AcumuladoDaConta>,
  chave: string,
  conta: ClassificacaoBrutaContasPagar | null,
  fatia: ValoresDaBaixa,
): void {
  const atual = mapa.get(chave) ?? { conta, fatias: [], baixas: 0 };
  atual.fatias.push(fatia);
  atual.baixas += 1;
  mapa.set(chave, atual);
}

/**
 * O resumo por conta contábil: as cinco colunas de cada baixa **divididas**
 * pelo índice de cada conta do título, com `ratearValoresDaBaixa`.
 *
 * O que o rateio não cobre — o título sem classificação, ou a classificação que
 * soma menos de 100% — vai para "Sem classificação", como o que sobrou. É o que
 * faz o resumo fechar no total em todas as colunas.
 */
function resumoPorContaContabil(
  baixas: readonly BaixaBrutaPagamentos[],
  classificacoesPorTitulo: ReadonlyMap<string, readonly ClassificacaoBrutaContasPagar[]>,
): z.infer<typeof contaContabilResumoPagamentosSchema>[] {
  const acumulados = new Map<string, AcumuladoDaConta>();

  for (const baixa of baixas) {
    const contas = classificacoesPorTitulo.get(baixa.contaPagarId) ?? [];
    const { fatias, resto } = ratearValoresDaBaixa(
      valoresDaBaixaBruta(baixa),
      contas.map((conta) => conta.indiceRateio),
    );

    contas.forEach((conta, indice) => {
      acumular(acumulados, conta.contaContabilId, conta, fatias[indice] as ValoresDaBaixa);
    });
    if (resto !== null) acumular(acumulados, SEM_CONTA, null, resto);
  }

  return [...acumulados]
    .map(([, { conta, fatias, baixas: quantas }]) => ({
      contaContabilId: conta?.contaContabilId ?? null,
      contaContabilCodigo: conta?.contaContabilCodigo ?? null,
      contaContabilDescricao: conta?.contaContabilDescricao ?? null,
      ...somarValoresDaBaixa(fatias, quantas),
    }))
    .sort(
      (a, b) =>
        (a.contaContabilId === null ? 1 : 0) - (b.contaContabilId === null ? 1 : 0) ||
        compararCodigo(a.contaContabilCodigo, b.contaContabilCodigo) ||
        COMPARAR_TEXTO_BAIXAS.compare(
          a.contaContabilDescricao ?? '',
          b.contaContabilDescricao ?? '',
        ),
    );
}

/**
 * Monta o relatório: quebra por empresa, agrupa por data de pagamento, fecha os
 * subtotais e os três resumos.
 *
 * No contrato, e não no service, porque é regra pura: as somas e a divisão por
 * conta contábil são onde o relatório pode perder um centavo, e o teste precisa
 * estar ali. Empresas em ordem alfabética; datas da mais antiga à mais nova.
 */
export function montarRelatorioPagamentos(
  baixas: readonly BaixaBrutaPagamentos[],
  classificacoes: readonly ClassificacaoBrutaContasPagar[],
): Pick<RelatorioPagamentos, 'empresas' | 'totais' | 'resumo'> {
  const classificacoesPorTitulo = new Map<string, ClassificacaoBrutaContasPagar[]>();
  for (const fatia of classificacoes) {
    const doTitulo = classificacoesPorTitulo.get(fatia.contaPagarId) ?? [];
    doTitulo.push(fatia);
    classificacoesPorTitulo.set(fatia.contaPagarId, doTitulo);
  }

  const linhas: LinhaRelatorioPagamentos[] = [];
  const porEmpresa = new Map<string, { nome: string; linhas: LinhaRelatorioPagamentos[] }>();
  for (const baixa of baixas) {
    const linha = paraLinha(baixa, classificacoesPorTitulo.get(baixa.contaPagarId) ?? []);
    linhas.push(linha);
    const empresa = porEmpresa.get(baixa.empresaId) ?? { nome: baixa.empresaNome, linhas: [] };
    empresa.linhas.push(linha);
    porEmpresa.set(baixa.empresaId, empresa);
  }

  const empresas = [...porEmpresa]
    .map(([empresaId, { nome, linhas: daEmpresa }]) => ({
      empresaId,
      empresaNome: nome,
      datas: quebrarPorDataDaBaixa(daEmpresa, (l) => l.dataPagamento, compararBaixas).map(
        ({ data, baixas: doDia, totais }) => ({ dataPagamento: data, baixas: doDia, totais }),
      ),
      totais: somarValoresDaBaixa(daEmpresa),
    }))
    .sort((a, b) => COMPARAR_TEXTO_BAIXAS.compare(a.empresaNome, b.empresaNome));

  return {
    empresas,
    totais: somarValoresDaBaixa(linhas),
    resumo: {
      porFornecedor: resumoPorFornecedor(linhas),
      porContaContabil: resumoPorContaContabil(baixas, classificacoesPorTitulo),
      porContaCorrente: resumoPorContaCorrente(
        baixas.map((baixa) => ({
          contaCorrenteId: baixa.contaCorrenteId,
          contaCorrenteDescricao: baixa.contaCorrenteDescricao,
          contaCorrenteEmpresaNome: baixa.contaCorrenteEmpresaNome,
          ...valoresDaBaixaBruta(baixa),
        })),
      ),
    },
  };
}
